import { AlertTriangle } from "lucide-react";
import { useFetcher } from "~/hooks/useFetcher";
import type { DatasetListItem } from "~/pages/datasets/types/interfaces/datasetListItem";
import type { ShareRecipient } from "~/pages/manifold-nextgen/components/agents/AgentShareRecipientWarning";
import { CopyAllEmailsButton } from "~/pages/manifold-nextgen/components/agents/CopyAllEmailsButton";
import {
  listMembers,
  memberCountPhrase,
  toolkitPhrase,
} from "~/pages/manifold-nextgen/components/agents/toolkitAccessCopy";
import type { Agent } from "~/services/agents";
import { groupPrincipal, userPrincipal } from "~/services/principals";
import {
  type ProjectShareResourceAccess,
  projectShareResourceAccessPath,
} from "~/services/projects";

interface ProjectShareRecipientWarningProps {
  projectId: string;
  recipient: ShareRecipient;
  // The project's attached agents and datasets as the caller sees them, to name
  // the ones the server reports.
  agents: Pick<Agent, "id" | "display_name">[] | undefined;
  datasets: Pick<DatasetListItem, "id" | "title">[] | undefined;
}

interface Warning {
  key: string;
  text: string;
  lackingEmails: string[];
}

// Warns, before sharing, that the recipient — or some members of a group —
// couldn't use some of the project's datasets, agents, or agents' toolkits. The server
// only reports resources the caller can use themselves.
export const ProjectShareRecipientWarning = ({
  projectId,
  recipient,
  agents,
  datasets,
}: ProjectShareRecipientWarningProps) => {
  const principal =
    recipient.type === "user" ? userPrincipal(recipient.id) : groupPrincipal(recipient.id);
  const { data } = useFetcher<ProjectShareResourceAccess>(
    projectShareResourceAccessPath(projectId, principal)
  );
  if (!data) {
    return null;
  }

  const who = (lacking: string[]) =>
    recipient.type === "user"
      ? recipient.name
      : `${memberCountPhrase(lacking.length, data.member_count)} of ${
          recipient.name
        } (${listMembers(lacking)})`;

  const warnings: Warning[] = [];
  if (data.datasets.length > 0) {
    const titles = data.datasets.map(
      (d) => datasets?.find((ds) => ds.id === d.dataset_id)?.title ?? d.dataset_id
    );
    const lacking = [...new Set(data.datasets.flatMap((d) => d.members_lacking_access))];
    const plural = titles.length > 1;
    warnings.push({
      key: "datasets",
      text: `${who(lacking)} won't be able to access this project's ${titles.join(", ")} ${
        plural ? "datasets" : "dataset"
      }. Please reach out to the ${plural ? "datasets' owners" : "dataset's owner"} to grant them access.`,
      lackingEmails: lacking,
    });
  }
  const agentName = (id: string) => agents?.find((agent) => agent.id === id)?.display_name ?? id;
  const unshared = data.agents.filter((a) => a.members_without_agent_access.length > 0);
  if (unshared.length > 0) {
    const names = unshared.map((a) => agentName(a.agent_id));
    const lacking = [...new Set(unshared.flatMap((a) => a.members_without_agent_access))];
    const plural = names.length > 1;
    warnings.push({
      key: "unshared-agents",
      text: `${who(lacking)} won't be able to use this project's ${names.join(", ")} ${
        plural ? "agents, which aren't" : "agent, which isn't"
      } shared with them. Please reach out to the ${
        plural ? "agents' owners" : "agent's owner"
      } to grant them access.`,
      lackingEmails: lacking,
    });
  }
  for (const a of data.agents.filter((a) => a.members_lacking_access.length > 0)) {
    const name = agentName(a.agent_id);
    warnings.push({
      key: `agent:${a.agent_id}`,
      text: `${who(a.members_lacking_access)} won't be able to use the ${name} agent's ${toolkitPhrase(
        a.toolkits.map((t) => t.display_name)
      )}. Please reach out to an admin to grant them access.`,
      lackingEmails: a.members_lacking_access,
    });
  }

  return (
    <>
      {warnings.map((w) => (
        <p key={w.key} className="flex items-start gap-1 text-xs text-amber-600">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span>{w.text}</span>
          {recipient.type === "group" && <CopyAllEmailsButton emails={w.lackingEmails} />}
        </p>
      ))}
    </>
  );
};
